import React, { useEffect, useRef, useState } from 'react';
import { Check, X, ArrowRight, Target, Shield, BarChart3, Layers, ShieldCheck, CalendarClock, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { OutputEstimator } from '../components/OutputEstimator';
import { PaymentSchedule } from '../components/PaymentSchedule';
import { AdvantageStructure } from '../components/AdvantageStructure';
import { AdvantageGuarantees } from '../components/AdvantageGuarantees';
import { usePricing } from '../contexts/PricingContext';
export function AdvantageProduct() {
  const {
    adSpend,
    cpaLow,
    cpaHigh,
    hasRecruiter,
    hasLeader,
    splitBuildFee,
    setAdSpend,
    setCpaLow,
    setCpaHigh,
    setHasRecruiter,
    setHasLeader,
    setSplitBuildFee,
    leaderBuildFee,
    leaderMonthlyFee,
    leaderActiveFee,
    leaderAdSpendLow,
    leaderAdSpendHigh,
    buildFee,
    scopeMultiplier,
    platformFee,
    campaignFee,
    recruiterFee,
    monthlyTotal,
    hiresLow,
    hiresHigh
  } = usePricing();
  const [showPayments, setShowPayments] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [revealed, setRevealed] = useState(1);
  const [pendingScroll, setPendingScroll] = useState<number | null>(null);
  const slideRefs = useRef<Array<HTMLElement | null>>([]);
  const heroRef = useRef<HTMLElement | null>(null);
  const [heroHeight, setHeroHeight] = useState(0);

  // The first panel fills whatever is left below the hero, at any window size.
  useEffect(() => {
    const element = heroRef.current;
    if (!element) return;
    const measure = () => setHeroHeight(element.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const slides = [{
    label: 'Output and Ad Spend',
    icon: BarChart3,
    content: <OutputEstimator adSpend={adSpend} cpaLow={cpaLow} cpaHigh={cpaHigh} onAdSpendChange={setAdSpend} onCpaLowChange={setCpaLow} onCpaHighChange={setCpaHigh} data-id="element-621" data-mp="20641xs9spt67" />
  }, {
    label: 'Partnership Structure',
    icon: Layers,
    content: <AdvantageStructure buildFee={buildFee} monthlyTotal={monthlyTotal} platformFee={platformFee} campaignFee={campaignFee} recruiterFee={recruiterFee} scopeMultiplier={scopeMultiplier} hasRecruiter={hasRecruiter} onRecruiterChange={setHasRecruiter} hasLeader={hasLeader} onLeaderChange={setHasLeader} leaderBuildFee={leaderBuildFee} leaderMonthlyFee={leaderMonthlyFee} leaderActiveFee={leaderActiveFee} splitBuildFee={splitBuildFee} onSplitBuildFeeChange={setSplitBuildFee} adSpend={adSpend} onAdSpendChange={setAdSpend} cpaLow={cpaLow} cpaHigh={cpaHigh} data-id="element-622" data-mp="3c9le6yjdibpy" />
  }, {
    label: showPayments ? 'Timelines and Payments' : 'Timelines',
    icon: CalendarClock,
    content: <div data-id="element-623" data-mp="geqihtoonemk">
          <PaymentSchedule buildFee={buildFee} monthlyService={monthlyTotal} adSpend={adSpend} hiresLow={hiresLow} hiresHigh={hiresHigh} splitBuildFee={splitBuildFee} onSplitBuildFeeChange={setSplitBuildFee} showPayments={showPayments} onShowPaymentsChange={setShowPayments} hasLeader={hasLeader} leaderBuildFee={leaderBuildFee} leaderMonthlyFee={leaderMonthlyFee} leaderActiveFee={leaderActiveFee} leaderAdSpendLow={leaderAdSpendLow} leaderAdSpendHigh={leaderAdSpendHigh} data-id="element-624" data-mp="gb2s33ejb8c2" />
        </div>
  }, {
    label: 'Guarantees and Agreements',
    icon: ShieldCheck,
    content: <AdvantageGuarantees data-id="element-625" data-mp="1it8pcd3794un" />
  }];

  // Panels stay in the document once revealed, so scrolling back up shows the
  // earlier slides. Navigating reveals up to the target and scrolls to it.
  const goToSlide = (index: number) => {
    setRevealed(current => Math.max(current, index + 1));
    setActiveSlide(index);
    setPendingScroll(index);
  };

  // Arrow keys / page keys advance and rewind the deck one panel per press.
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'PageDown';
      const backward = event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'PageUp';
      if (!forward && !backward) {
        return;
      }
      event.preventDefault();
      setActiveSlide(current => {
        const next = Math.min(slides.length - 1, Math.max(0, current + (forward ? 1 : -1)));
        if (next !== current) {
          setRevealed(revealedCount => Math.max(revealedCount, next + 1));
          setPendingScroll(next);
        }
        return next;
      });
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [slides.length]);
  useEffect(() => {
    if (pendingScroll === null) {
      return;
    }
    const node = slideRefs.current[pendingScroll];
    if (node) {
      node.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    setPendingScroll(null);
  }, [pendingScroll, revealed]);
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 selection:bg-[#7FE0C3]/30 font-sans" data-id="element-626" data-mp="21rlc29jqztf1">
      {/* 1. HERO */}
      <section ref={heroRef} className="relative pt-16 pb-0 px-6 md:px-12 max-w-7xl mx-auto text-center" data-id="element-627" data-mp="25sezz2x6x87x">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-[#7FE0C3]/5 blur-[120px] rounded-full pointer-events-none" data-id="element-628" data-mp="6oyewu8qb54d" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-629" data-mp="fgi4oqve24n7">
          <div className="inline-block px-3 py-1 rounded-full bg-[#7FE0C3]/10 border border-[#7FE0C3]/20 text-[#7FE0C3] text-xs font-bold uppercase tracking-widest mb-4" data-id="element-630" data-mp="pdd1n5c0jwzd">
            Flagship Partnership
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tight" data-id="element-631" data-mp="ydi9fgl4cxe2">
            HIRE Advantage
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto leading-relaxed" data-id="element-632" data-mp="306pmskh694xb">
            HIRE Advantage is the partnership where recruiting becomes
            consistent, predictable, and scalable.
          </p>
        </motion.div>
      </section>

      {/* 2. THE DECK */}
      <div className="relative" data-id="element-633" data-mp="2q312j1xahljs">
        {/* Icon rail — floats at the left edge without displacing the slides */}
        <nav aria-label="Partnership deck" className="hidden lg:flex fixed left-3 top-1/2 -translate-y-1/2 z-20 flex-col gap-1" data-id="element-634" data-mp="2cc5u1qqpd6gq">
          {slides.map((slide, index) => {
          const Icon = slide.icon;
          const isActive = index === activeSlide;
          return <button key={slide.label} type="button" onClick={() => goToSlide(index)} aria-current={isActive ? 'step' : undefined} aria-label={slide.label} title={slide.label} className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${isActive ? 'bg-[#7FE0C3]/10 text-[#7FE0C3]' : 'text-slate-700 hover:text-slate-300'}`} data-id="element-635" data-mp="27ct3ow0mhh9e" data-mpi={index}>
                <Icon className="w-5 h-5" data-id="element-636" data-mp="105ufumi3u2lp" />
              </button>;
        })}
        </nav>

        {slides.map((slide, index) => {
        if (index >= revealed) {
          return null;
        }
        const nextSlide = slides[index + 1];
        return <section key={slide.label} ref={node => {
          slideRefs.current[index] = node;
        }} aria-label={slide.label} className={`flex flex-col justify-center px-6 md:px-12 ${index === 0 ? 'pt-2 pb-8' : 'min-h-screen py-12'}`} style={index === 0 && heroHeight ? {
          minHeight: `calc(100vh - ${heroHeight}px)`
        } : undefined} data-id="element-637" data-mp="rbppe46vut6p" data-mpi={index}>
              <div className="w-full max-w-[1400px] mx-auto" data-id="element-638" data-mp="26ry7jtl5glr0">
                {/* Icon row for small screens */}
                <div className="flex lg:hidden justify-center gap-1 mb-6" data-id="element-639" data-mp="3kqkea5tzloy4">
                  {slides.map((item, itemIndex) => {
                const Icon = item.icon;
                return <button key={item.label} type="button" onClick={() => goToSlide(itemIndex)} aria-label={item.label} className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${itemIndex === index ? 'bg-[#7FE0C3]/10 text-[#7FE0C3]' : 'text-slate-600 hover:text-slate-300'}`} data-id="element-640" data-mp="3ugdn4z2qvpyo" data-mpi={itemIndex}>
                        <Icon className="w-5 h-5" data-id="element-641" data-mp="3fssphkon2iy5" />
                      </button>;
              })}
                </div>

                {slide.content}

                {nextSlide && <div className="mt-10 flex justify-center" data-id="element-642" data-mp="uz5pyhlplxjg">
                    <button type="button" onClick={() => goToSlide(index + 1)} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-widest text-slate-400 border border-slate-800 hover:border-[#7FE0C3]/40 hover:text-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-643" data-mp="2ic8v7j6q5ijz">
                      {nextSlide.label}
                      <ChevronDown className="w-4 h-4" data-id="element-644" data-mp="24cr42x5yht7g" />
                    </button>
                  </div>}
              </div>
            </section>;
      })}
      </div>

      {/* 3. PROBLEM REFRAME */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto border-t border-slate-800/50" data-id="element-645" data-mp="2ddpa1kzygaso">
        <div className="text-center mb-12" data-id="element-646" data-mp="3kpenweu9dda5">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-647" data-mp="3or9m3agw7ajp">
            The Real Problem
          </h2>
          <p className="text-slate-400 text-lg" data-id="element-648" data-mp="1d4pldfe2z45x">
            Most companies don't fail at finding candidates. They fail at system
            execution.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-649" data-mp="14bw113js1el7">
          <div className="bg-slate-900/30 border border-slate-800 p-8 rounded-xl" data-id="element-650" data-mp="14a1g3p8i9mh4">
            <h3 className="text-sm text-rose-400 uppercase tracking-widest font-bold mb-6" data-id="element-651" data-mp="zbzyz9un3x8a">
              System Failures
            </h3>
            <ul className="space-y-4" data-id="element-652" data-mp="ubby7wgm020v">
              {['Inconsistent interviews', 'Drifting hiring standards', 'Early churn discovered too late', 'Leaders relying on gut decisions'].map((item, idx) => <li key={idx} className="flex items-start text-slate-400" data-id="element-653" data-mp="gg03vq6d8dkh" data-mpi={idx}>
                  <X className="w-5 h-5 text-rose-500/50 mr-3 mt-0.5 flex-shrink-0" data-id="element-654" data-mp="2fqj1muake4oz" />
                  <span className="line-through opacity-70" data-id="element-655" data-mp="2aszpq11ca407">{item}</span>
                </li>)}
            </ul>
          </div>
          <div className="flex flex-col justify-center p-8" data-id="element-656" data-mp="2tugq2pyroz26">
            <p className="text-xl text-white font-medium mb-4" data-id="element-657" data-mp="2yo32qi4xl2xz">
              Advantage solves the system failure, not just the talent shortage.
            </p>
            <p className="text-slate-400" data-id="element-658" data-mp="2puq0uqow2qzr">
              We replace guesswork with infrastructure and accountability.
            </p>
          </div>
        </div>
      </section>

      {/* 4. WHAT ADVANTAGE IS */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-659" data-mp="3hmnp5wvvnk5a">
        <div className="max-w-5xl mx-auto" data-id="element-660" data-mp="33mmmcbw1y2n6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-661" data-mp="33lurx4e6yayh">
            <div data-id="element-662" data-mp="38keiji3qcax6">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-663" data-mp="38jwbrrjfyeb0">
                Full System Ownership
              </h2>
              <div className="space-y-4 text-slate-400 mb-8" data-id="element-664" data-mp="1s9k8vpp91t78">
                <p data-id="element-665" data-mp="1x73l6k2dzvwj">This is not lead generation.</p>
                <p data-id="element-666" data-mp="1pmrlol4dtysm">This is not outsourced recruiting.</p>
                <p data-id="element-667" data-mp="1pl86yyydjvsy">This is full ownership of the recruiting system.</p>
              </div>
              <div className="p-6 bg-[#7FE0C3]/5 border border-[#7FE0C3]/20 rounded-lg" data-id="element-668" data-mp="1kmwwqn463ja7">
                <p className="text-[#7FE0C3] font-medium" data-id="element-669" data-mp="1ymeq7fserkpw">
                  HIRE Advantage is the only partnership level where HIRE is
                  accountable for the recruiting system actually working.
                </p>
              </div>
            </div>
            <div className="space-y-6" data-id="element-670" data-mp="11xgvtyoklot9">
              <div className="flex items-start" data-id="element-671" data-mp="11yi3ruaahv6r">
                <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-672" data-mp="1folmib9yn6rg">
                  <Shield className="w-6 h-6 text-[#7FE0C3]" data-id="element-673" data-mp="1ax3lxobun870" />
                </div>
                <div data-id="element-674" data-mp="n79936rj5jmr">
                  <h3 className="text-white font-bold mb-2" data-id="element-675" data-mp="n6q40qmzapq5">Accountability</h3>
                  <p className="text-slate-400 text-sm" data-id="element-676" data-mp="i86gilsql5vo">
                    We don't just deliver leads; we own the outcome of the
                    system's performance.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-677" data-mp="10sx6wlho2yu0">
                <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-678" data-mp="15tun4sj49isv">
                  <Target className="w-6 h-6 text-[#7FE0C3]" data-id="element-679" data-mp="1amxk7an3radt" />
                </div>
                <div data-id="element-680" data-mp="1elwkghxw2iyx">
                  <h3 className="text-white font-bold mb-2" data-id="element-681" data-mp="1eoivwsnwhimf">Consistency</h3>
                  <p className="text-slate-400 text-sm" data-id="element-682" data-mp="1jjy4p671wobz">
                    Eliminating the variance between good weeks and bad weeks.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. 10-STEP SYSTEM */}
      <section className="py-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-683" data-mp="3qda92u1n18zp">
        <div className="text-center mb-12" data-id="element-684" data-mp="3vajyi9p11knj">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-685" data-mp="3vbbv9nud1ofe">
            The 10-Step Recruiting System
          </h2>
          <p className="text-slate-400" data-id="element-686" data-mp="2chs7pii057ku">
            Every HIRE Advantage partner runs the same system. The difference is
            who owns it.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-687" data-mp="23o5he7wkcwj1">
          {['Attract', 'Engage', 'Screen', 'Qualify', 'Commit', 'Schedule', 'Launch', 'Integrate', 'Train', 'Retain'].map((step, idx) => <div key={idx} className="bg-slate-900/40 border border-slate-800 p-6 rounded-lg text-center hover:border-[#7FE0C3]/30 transition-colors group" data-id="element-688" data-mp="2me526ildfpgn" data-mpi={idx}>
              <div className="text-xs text-slate-500 mb-2 font-mono" data-id="element-689" data-mp="1ayydsui95gqx">
                0{idx + 1}
              </div>
              <div className="text-white font-medium group-hover:text-[#7FE0C3] transition-colors" data-id="element-690" data-mp="15zc2wrpwccla">
                {step}
              </div>
            </div>)}
        </div>
      </section>

      {/* 6. OWNERSHIP MAP */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-691" data-mp="1ceu265dhh7cz">
        <div className="max-w-6xl mx-auto" data-id="element-692" data-mp="yfkobccr2pc6">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-693" data-mp="yioxyp8zida8">
            Ownership Map
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16" data-id="element-694" data-mp="oqszve6wen60">
            {/* HIRE Owns */}
            <div className="bg-slate-900/40 border border-[#7FE0C3]/20 rounded-xl p-8 relative overflow-hidden" data-id="element-695" data-mp="thsycnvo1dam">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3]" data-id="element-696" data-mp="fqnwrg8qmaxn" />
              <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-697" data-mp="jr6lh1fc30vm">
                <span className="w-2 h-2 rounded-full bg-[#7FE0C3] mr-3" data-id="element-698" data-mp="5roucfekfbj3" />
                HIRE Owns
              </h3>
              <ul className="space-y-4" data-id="element-699" data-mp="arb8cr27wvsy">
                {['Recruiting system architecture (custom ATS)', 'Traffic, screening, and qualification standards', 'Hiring consistency and interview structure', 'Launch readiness and onboarding', 'Early performance tracking and correction', 'Ongoing optimization and accountability'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-700" data-mp="a3ucbub5wcwk" data-mpi={idx}>
                    <Check className="w-5 h-5 text-[#7FE0C3] mr-3 mt-0.5 flex-shrink-0" data-id="element-701" data-mp="a1rckik7j5en" />
                    <span data-id="element-702" data-mp="54he6deubjeo">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* You Own */}
            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-8 relative overflow-hidden" data-id="element-703" data-mp="euua11s3uz04">
              <div className="absolute top-0 left-0 w-full h-1 bg-slate-600" data-id="element-704" data-mp="sttxw77sn9v8" />
              <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-705" data-mp="osqkrsh4rrxp">
                <span className="w-2 h-2 rounded-full bg-slate-600 mr-3" data-id="element-706" data-mp="oqx8nxnr0fib" />
                You Own
              </h3>
              <ul className="space-y-4" data-id="element-707" data-mp="206j09a9pa686">
                {['Final hiring decisions', 'Day-to-day sales leadership', 'Culture and performance expectations'].map((item, idx) => <li key={idx} className="flex items-start text-slate-400" data-id="element-708" data-mp="296xp2jlvi3gf" data-mpi={idx}>
                    <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center" data-id="element-709" data-mp="1ci9koq32yeph">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-500" data-id="element-710" data-mp="1lhwmhczq1mj2" />
                    </div>
                    <span data-id="element-711" data-mp="1q8lectfbcliq">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7. WHO THIS IS FOR */}
      <section className="py-20 px-6 md:px-12 max-w-5xl mx-auto" data-id="element-712" data-mp="tow4xs7xueug">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-713" data-mp="xqr8l3dcd4be">
          <div data-id="element-714" data-mp="q91dwwauf8m0">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-715" data-mp="q7hxn5p6sxk8">
              <Check className="w-5 h-5 text-[#7FE0C3] mr-2" data-id="element-716" data-mp="l9ge6jnumw88" />
              This is for teams who:
            </h3>
            <ul className="space-y-4" data-id="element-717" data-mp="h6s4yav83naq">
              {['Want recruiting to actually work', 'Value consistency over heroics', 'Are open to better systems', 'Want a partner, not a vendor'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-718" data-mp="2i5qschc11l3" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#7FE0C3] mr-3 mt-2" data-id="element-719" data-mp="3tp04pax4y0jl" />
                  {item}
                </li>)}
            </ul>
          </div>
          <div data-id="element-720" data-mp="3pm3jdcg7nqby">
            <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-721" data-mp="3kok366zyd3hd">
              <X className="w-5 h-5 text-rose-500 mr-2" data-id="element-722" data-mp="3fpqoib8xj6fm" />
              This is not for teams who:
            </h3>
            <ul className="space-y-4" data-id="element-723" data-mp="3eh40fe3nr5uu">
              {['Only want resumes', "Aren't open to process", 'Want zero accountability'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-724" data-mp="34iet2f9e13su" data-mpi={idx}>
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-3 mt-2" data-id="element-725" data-mp="2zkdadvfvj6hw" />
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* 8. HOW THIS SCALES */}
      <section className="py-16 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800 text-center" data-id="element-726" data-mp="2qr1c992v6k1i">
        <div className="max-w-3xl mx-auto" data-id="element-727" data-mp="2lthzyepq8hc7">
          <h2 className="text-2xl font-bold text-white mb-8" data-id="element-728" data-mp="2gtn92b5ybrr5">
            How This Scales
          </h2>
          <div className="flex flex-col md:flex-row justify-center gap-6 text-left" data-id="element-729" data-mp="2buiwdajtjbw2">
            <div className="flex-1 p-6 border border-[#7FE0C3]/30 rounded-lg bg-slate-800/30" data-id="element-730" data-mp="2bwd941kxrxp2">
              <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-2" data-id="element-731" data-mp="27vb8phfa9fli">
                Foundation
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-732" data-mp="rbyn7uda3eoo">Advantage</h3>
              <p className="text-slate-400 text-sm" data-id="element-733" data-mp="recfxwkhket2">
                The foundation your recruiting runs on.
              </p>
            </div>
            <div className="flex-1 p-6 border border-slate-800 rounded-lg bg-slate-900/40" data-id="element-734" data-mp="119mkaqycwya0">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-735" data-mp="3noyokookctu">
                Execution
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-736" data-mp="8m9y4hw3ysh8">Corporate Recruiter</h3>
              <p className="text-slate-400 text-sm" data-id="element-737" data-mp="dkbg0zhsslr4">
                Add it when execution capacity becomes the bottleneck.
              </p>
            </div>
            <div className="flex-1 p-6 border border-slate-800 rounded-lg bg-slate-900/40" data-id="element-738" data-mp="3h23emwqfw3ya">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-739" data-mp="3m0ws4p5q7uyg">
                Leadership
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-740" data-mp="3dzfi7lehrzk7">Leadership</h3>
              <p className="text-slate-400 text-sm" data-id="element-741" data-mp="39yvhy8ymgftu">
                Add it when finding leadership becomes the bottleneck.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. CTA */}
      <section className="py-32 px-6 md:px-12 text-center" data-id="element-742" data-mp="3iqnpaascn8u7">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-743" data-mp="3it9yeeuw1wej">
          Ready to make recruiting predictable?
        </h2>
        <button className="inline-flex items-center px-8 py-4 bg-[#7FE0C3] hover:bg-[#5ACCA8] text-[#163A5F] font-bold rounded-lg transition-colors duration-150 ease-out" data-id="element-744" data-mp="2r0j8sn7pn5y9">
          Let's see if Advantage is the right fit
          <ArrowRight className="ml-2 w-5 h-5" data-id="element-745" data-mp="2zzo83vr3j3bh" />
        </button>
      </section>
    </div>;
}